import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/shell/ProtectedRoute";
import CallBadge from "./components/CallBadge";
import VersionWatcher from "./components/VersionWatcher";
import { hydrateBenchmarks } from "./lib/propertyTypes";
import api from "./lib/api";
import { Toaster } from "sonner";

import Landing from "./pages/Landing";
import About from "./pages/About";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import TenantLogin from "./pages/TenantLogin";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Properties from "./pages/Properties";
import RetakePhotoBatch from "./pages/RetakePhotoBatch";
import Help from "./pages/Help";
import Tenants from "./pages/Tenants";
import Leases from "./pages/Leases";
import LeaseVault from "./pages/LeaseVault";
import Mailbox from "./pages/Mailbox";
import Messages from "./pages/Messages";
import Schedule from "./pages/Schedule";
import Workspaces from "./pages/Workspaces";
import Expenses from "./pages/Expenses";
import Loans from "./pages/Loans";
import Reports from "./pages/Reports";
import Valuation from "./pages/Valuation";
import PersonalFinancialStatement from "./pages/PersonalFinancialStatement";
import SharedPfs from "./pages/SharedPfs";
import SharedLenderPackage from "./pages/SharedLenderPackage";
import Comps from "./pages/Comps";
import PortfolioPerformance from "./pages/PortfolioPerformance";
import InvestorReport from "./pages/InvestorReport";
import Unsubscribe from "./pages/Unsubscribe";
import RenewalProposal from "./pages/RenewalProposal";
import Settings from "./pages/Settings";
import Vendors from "./pages/Vendors";
import Maintenance from "./pages/Maintenance";
import Payments from "./pages/Payments";
import VendorPortal from "./pages/VendorPortal";
import Leads from "./pages/Leads";
import Listing from "./pages/Listing";
import FindSpace from "./pages/FindSpace";
import AdminRevenue from "./pages/AdminRevenue";
import AdminMarketing from "./pages/admin/Marketing";
import AdminBlogEditor from "./pages/admin/BlogEditor";
import BlogIndex from "./pages/public/BlogIndex";
import BlogPost from "./pages/public/BlogPost";
import BlogAuthor from "./pages/public/BlogAuthor";
import AdminDemoRequests from "./pages/AdminDemoRequests";
import AdminIntegrations from "./pages/AdminIntegrations";
import AdminLandlords from "./pages/AdminLandlords";
import AdminLandlordActivity from "./pages/AdminLandlordActivity";
import AdminAuditLog from "./pages/AdminAuditLog";
import Billing from "./pages/Billing";
import TenantDashboard from "./pages/TenantDashboard";
import PaymentSuccess from "./pages/PaymentSuccess";
import PayRent from "./pages/PayRent";
import VendorSignup from "./pages/VendorSignup";
import LoginAlert from "./pages/LoginAlert";
import MarketingKit from "./pages/MarketingKit";
import Deals from "./pages/Deals";
import PublicDeal from "./pages/PublicDeal";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Landing />;
  if (user.role === "tenant") return <Navigate to="/tenant" replace />;
  return <Navigate to="/dashboard" replace />;
}

function App() {
  useEffect(() => {
    // Hydrate live cap-rate benchmarks from the server at app boot.
    // Fails silently → falls back to defaults baked into propertyTypes.js.
    hydrateBenchmarks(api);
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <CallBadge />
          <VersionWatcher />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/author/:slug" element={<BlogAuthor />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/about" element={<About />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/tenant-login" element={<TenantLogin />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/share/pfs/:token" element={<SharedPfs />} />
            <Route path="/vendor-portal/:token" element={<VendorPortal />} />
            <Route path="/share/lender-package/:token" element={<SharedLenderPackage />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/pay/:token" element={<PayRent />} />
            <Route path="/vendor-signup/:token" element={<VendorSignup />} />
            <Route path="/security/login-alert/:token" element={<LoginAlert />} />
            <Route path="/listings/:id" element={<Listing />} />
            <Route path="/find-space" element={<FindSpace />} />
            <Route path="/investor/:hash" element={<InvestorReport />} />
            <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/renewal-proposal/:token" element={<RenewalProposal />} />
            <Route path="/marketing-kit" element={<MarketingKit />} />
            <Route path="/deal/view/:token" element={<PublicDeal />} />

            <Route path="/dashboard" element={<ProtectedRoute role="landlord"><Dashboard /></ProtectedRoute>} />
            <Route path="/dashboard/properties" element={<ProtectedRoute role="landlord"><Properties /></ProtectedRoute>} />
            <Route path="/dashboard/deals" element={<ProtectedRoute role="admin"><Deals /></ProtectedRoute>} />
            <Route path="/dashboard/properties/retake" element={<ProtectedRoute role="landlord"><RetakePhotoBatch /></ProtectedRoute>} />
            <Route path="/dashboard/tenants" element={<ProtectedRoute role="landlord"><Tenants /></ProtectedRoute>} />
            <Route path="/dashboard/leases" element={<ProtectedRoute role="landlord"><Leases /></ProtectedRoute>} />
            <Route path="/dashboard/leases/vault" element={<ProtectedRoute role="landlord"><LeaseVault /></ProtectedRoute>} />
            <Route path="/dashboard/mail/inbox" element={<ProtectedRoute role="landlord"><Mailbox mode="inbox" /></ProtectedRoute>} />
            <Route path="/dashboard/mail/sent" element={<ProtectedRoute role="landlord"><Mailbox mode="sent" /></ProtectedRoute>} />
            <Route path="/dashboard/messages" element={<ProtectedRoute role="landlord"><Messages /></ProtectedRoute>} />
            <Route path="/dashboard/schedule" element={<ProtectedRoute role="landlord"><Schedule /></ProtectedRoute>} />
            <Route path="/dashboard/workspaces" element={<ProtectedRoute role="landlord"><Workspaces /></ProtectedRoute>} />
            <Route path="/dashboard/expenses" element={<ProtectedRoute role="landlord"><Expenses /></ProtectedRoute>} />
            <Route path="/dashboard/loans" element={<ProtectedRoute role="landlord"><Loans /></ProtectedRoute>} />
            <Route path="/dashboard/vendors" element={<ProtectedRoute role="landlord"><Vendors /></ProtectedRoute>} />
            <Route path="/dashboard/maintenance" element={<ProtectedRoute role="landlord"><Maintenance /></ProtectedRoute>} />
            <Route path="/dashboard/payments" element={<ProtectedRoute role="landlord"><Payments /></ProtectedRoute>} />
            <Route path="/dashboard/leads" element={<ProtectedRoute role="landlord"><Leads /></ProtectedRoute>} />
            <Route path="/dashboard/reports" element={<ProtectedRoute role="landlord"><Reports /></ProtectedRoute>} />
            <Route path="/dashboard/valuation" element={<ProtectedRoute role="landlord"><Valuation /></ProtectedRoute>} />
            <Route path="/dashboard/pfs" element={<ProtectedRoute role="landlord"><PersonalFinancialStatement /></ProtectedRoute>} />
            <Route path="/dashboard/comps" element={<ProtectedRoute role="landlord"><Comps /></ProtectedRoute>} />
            <Route path="/dashboard/performance" element={<ProtectedRoute role="landlord"><PortfolioPerformance /></ProtectedRoute>} />
            <Route path="/dashboard/settings" element={<ProtectedRoute role="landlord"><Settings /></ProtectedRoute>} />
            <Route path="/dashboard/admin/revenue" element={<ProtectedRoute role="admin"><AdminRevenue /></ProtectedRoute>} />
            <Route path="/dashboard/admin/marketing" element={<ProtectedRoute role="admin"><AdminMarketing /></ProtectedRoute>} />
            <Route path="/dashboard/admin/blog" element={<ProtectedRoute role="admin"><AdminBlogEditor /></ProtectedRoute>} />
            <Route path="/dashboard/admin/demo-requests" element={<ProtectedRoute role="admin"><AdminDemoRequests /></ProtectedRoute>} />
            <Route path="/dashboard/admin/integrations" element={<ProtectedRoute role="admin"><AdminIntegrations /></ProtectedRoute>} />
            <Route path="/dashboard/admin/landlords" element={<ProtectedRoute role="admin"><AdminLandlords /></ProtectedRoute>} />
            <Route path="/dashboard/admin/landlords/:lid/activity" element={<ProtectedRoute role="admin"><AdminLandlordActivity /></ProtectedRoute>} />
            <Route path="/dashboard/admin/audit-log" element={<ProtectedRoute role="admin"><AdminAuditLog /></ProtectedRoute>} />
            <Route path="/dashboard/billing" element={<ProtectedRoute role="landlord"><Billing /></ProtectedRoute>} />
            <Route path="/dashboard/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
            <Route path="/tenant/help" element={<ProtectedRoute role="tenant"><Help /></ProtectedRoute>} />

            <Route path="/tenant" element={<ProtectedRoute role="tenant"><TenantDashboard /></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
